import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PhoneCall, ChevronLeft, CheckCircle2, RotateCcw, Smartphone } from 'lucide-react';
import DwShell from '../components/home/dw/DwShell';
import { useAuth } from '../contexts/AuthContext';
import { CallingSetup } from '../components/calling/CallingSetup';
import { CallCard } from '../components/calling/CallCard';
import { QueueList } from '../components/calling/QueueList';

const API = process.env.REACT_APP_BACKEND_URL;

const Done = ({ session, onRequeue, onNew }) => (
  <section className="rounded-3xl border border-emerald-200 bg-emerald-50/60 p-6 text-center" data-testid="call-session-done">
    <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
    <h2 className="mt-2 text-2xl font-black text-slate-900">Session {session.status === 'finished' ? 'finished' : 'complete'}</h2>
    <p className="mt-1 text-sm text-slate-600">{session.done} of {session.total} called{session.skipped ? ` · ${session.skipped} skipped` : ''}. Every call has been saved to the client's activity log.</p>
    <div className="mt-4 flex flex-wrap justify-center gap-2">
      {!!session.skipped && <button type="button" onClick={onRequeue} data-testid="call-requeue-skipped" className="inline-flex items-center gap-1.5 rounded-full bg-[#15133A] px-4 py-2 text-sm font-bold text-white"><RotateCcw className="h-4 w-4" /> Call the {session.skipped} skipped</button>}
      <button type="button" onClick={onNew} data-testid="call-new-session" className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700">Start a new queue</button>
    </div>
  </section>
);

export default function CallingQueue() {
  const { token, user } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const sid = params.get('session');
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!sid) { setSession(null); return; }
    axios.get(`${API}/api/calling/sessions/${sid}`, { headers }).then((r) => setSession(r.data)).catch(() => setParams({}));
  }, [sid, headers, setParams]);
  useEffect(() => { load(); if (!sid) return undefined; const t = setInterval(load, 8000); return () => clearInterval(t); }, [load, sid]);

  const open = (s) => { setSession(s); setParams({ session: s.id }); };
  const post = async (path, body, msg) => {
    setBusy(true);
    try { const r = await axios.post(`${API}/api/calling/sessions/${sid}/${path}`, body || {}, { headers }); setSession(r.data); if (msg) toast.success(msg); return r.data; }
    catch (e) { toast.error(e.response?.data?.detail || 'Something went wrong'); return null; }
    finally { setBusy(false); }
  };

  const item = session && session.items[session.cursor];
  const finished = session && (session.status === 'finished' || session.remaining === 0);

  return (
    <DwShell activeKey="client-portal">
      <div className="mx-auto max-w-5xl px-3 pb-36 pt-3 sm:px-6 sm:py-8" data-testid="calling-page">
        <div className={`flex flex-wrap items-center justify-between gap-3 ${session ? 'mb-3 sm:mb-5' : 'mb-5'}`}>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => nav('/onboarding')} className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100" data-testid="calling-back"><ChevronLeft className="h-5 w-5" /></button>
            <div>
              <p className="text-[11px] font-black uppercase tracking-wider text-slate-500">Client Portal</p>
              <h1 className="flex items-center gap-2 text-xl font-black text-slate-900 sm:text-3xl"><PhoneCall className="h-5 w-5 text-emerald-600 sm:h-6 sm:w-6" /> Calling queue</h1>
            </div>
          </div>
          <p className="hidden max-w-sm items-start gap-1.5 text-xs text-slate-500 md:flex" data-testid="calling-phone-tip"><Smartphone className="mt-0.5 h-4 w-4 shrink-0" />On your phone, sign in to DerwentAI and open Client Portal, then Start Calling. Your session syncs, so you can pick up the same queue there and tap Call to use your normal mobile service.</p>
        </div>

        {!sid && <CallingSetup headers={headers} onStarted={open} me={user} initialSource={params.get('source')} />}
        {sid && !session && <p className="text-sm text-slate-500">Loading session…</p>}
        {session && finished && <Done session={session} onRequeue={async () => { try { const r = await axios.post(`${API}/api/calling/sessions/${sid}/requeue-skipped`, {}, { headers }); open(r.data); } catch (e) { toast.error(e.response?.data?.detail || 'Could not re-queue skipped clients'); } }} onNew={() => setParams({})} />}
        {session && !finished && item && (
          <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
            <CallCard session={session} item={item} busy={busy}
              onDial={() => post('dial', { index: item.index })}
              onOutcome={(outcome, note) => post('outcome', { index: item.index, outcome, note }, `Saved: ${session.outcomes[outcome]}`)}
              onSkip={() => post('skip', { index: item.index })}
              onNote={(note) => post('note', { index: item.index, note }, 'Note saved to the client file')}
              onPause={async () => { await post('pause', {}, 'Paused. Resume any time from Start Calling'); setParams({}); }}
              onFinish={() => post('finish', {}, 'Session finished')}
              onAppt={async (at) => { try { await axios.post(`${API}/api/calling/appointment`, { application_id: item.application_id, at }, { headers }); toast.success(at ? 'Appointment saved' : 'Appointment cleared'); load(); } catch { toast.error('Could not save appointment'); } }} />
            <QueueList session={session} onGoto={(index) => post('goto', { index })} />
          </div>
        )}
      </div>
    </DwShell>
  );
}
